import { memo, useCallback, useEffect, useState } from "react";
import { FileHelper, StringHelper, getFileIcon } from "../../helper";
import { Image } from "antd";
import {
  HiOutlineArrowDownTray,
  HiOutlineEye,
  HiOutlineTrash,
} from "react-icons/hi2";
import classNames from "classnames";

interface PreviewBeforeUploadProps {
  items: (string | File)[];
  isRemove: boolean;
  isDownload: boolean;
  onChange: (values: (File | string)[]) => void;
}

interface Item {
  raw: File | string;
  fileName: string;
  src: string;
  type: "image" | "file";
}

export const PreviewBeforeUpload = memo(
  (props: Partial<PreviewBeforeUploadProps>) => {
    const {
      items: itemsProp,
      isRemove = false,
      isDownload = false,
      onChange = () => {},
    } = props;
    const [items, setItems] = useState<Item[]>([]);

    const onRemove = (fileName: string) => {
      const removedFile = items
        .filter((e) => e.fileName !== fileName)
        .map((e) => e.raw);
      onChange(removedFile);
    };
    const filePathsHandler = useCallback((filePaths: string[] = []) => {
      return filePaths.map<Item>((path: string) => {
        if (StringHelper.isImage(path)) {
          return { raw: path, fileName: path, src: path, type: "image" };
        }
        return { raw: path, fileName: path, src: path, type: "file" };
      });
    }, []);
    const fileHandler = useCallback(async (files: File[] = []) => {
      return await Promise.all(
        files.map(async (file): Promise<Item> => {
          if (!file?.type?.startsWith("image")) {
            return { raw: file, fileName: file.name, src: "", type: "file" };
          }
          const base64 = await FileHelper.getBase64(file);
          return { raw: file, fileName: file.name, src: base64, type: "image" };
        })
      );
    }, []);

    const preview = (fileName: string) => {
      return {
        mask: (
          <div className="flex-center" style={{ gap: "1rem" }}>
            <HiOutlineEye size={18} className="cursor-pointer" />
            {isRemove && (
              <HiOutlineTrash
                size={18}
                className="cursor-pointer"
                onClick={(event) => {
                  event.stopPropagation();
                  onRemove(fileName);
                }}
              />
            )}
            {isDownload && (
              <a
                href={fileName}
                style={{ color: "white" }}
                onClick={(e) => e.stopPropagation()}
              >
                <HiOutlineArrowDownTray size={18} className="cursor-pointer" />
              </a>
            )}
          </div>
        ),
      };
    };

    useEffect(() => {
      (async () => {
        const filePaths = (itemsProp ?? []).filter(
          (e) => typeof e === "string"
        ) as string[];
        const files = (itemsProp ?? []).filter(
          (e: any) => !filePaths.includes(e)
        ) as File[];

        const [fileItems, filePathItems] = await Promise.all([
          fileHandler(files),
          Promise.resolve(filePathsHandler(filePaths)),
        ]);
        setItems(fileItems.concat(filePathItems));
      })();
    }, [itemsProp, fileHandler, filePathsHandler]);

    const imageList = items.filter((item) => item.type === "image");
    const fileList = items.filter((item) => item.type === "file");

    return (
      <div style={{ padding: "0 1rem" }}>
        {fileList.map(({ fileName, src }) => {
          return (
            <div
              key={fileName}
              className="flex-start"
              style={{ gap: 8, marginBottom: 10 }}
            >
              <span>{getFileIcon(fileName)}</span>
              <span
                className={classNames({ "cursor-pointer": src })}
                style={{ fontWeight: 500, fontSize: 16 }}
                onClick={() => {
                  if (src) window.open(src, "_blank");
                }}
              >
                {StringHelper.getNameFromFileUrl(fileName)}
              </span>
              {isRemove && (
                <HiOutlineTrash
                  size={16}
                  color="#ef4444"
                  className="cursor-pointer"
                  onClick={() => onRemove(fileName)}
                />
              )}
            </div>
          );
        })}
        <div className="flex" style={{ gap: 5 }}>
          <Image.PreviewGroup>
            {imageList.map(({ src, fileName }) => (
              <Image
                key={fileName}
                width={160}
                src={src}
                preview={preview(fileName)}
              />
            ))}
          </Image.PreviewGroup>
        </div>
      </div>
    );
  }
);
